import { FunctionComponent, ReactElement, useCallback, useState } from "react";

import {
    Divider,
    FormControlLabel,
    MenuItem,
    Switch,
    TextField,
    Tooltip,
    Typography,
    styled,
} from "@mui/material";

import { InfoOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { DateTimePicker } from "@mui/x-date-pickers";

import {
    useClearStatusOverride,
    useMessage,
    useSetStatusOverride,
    useUpdateVariantThresholds,
} from "../../sdk/hooks";
import {
    IVariantInventory,
    StockOverrideReason,
    StockStatus,
    stockOverrideReasonMap,
} from "../../sdk/utils/api";

const Root = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};

    margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Block = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1.5)};
`;

const BlockHeader = styled("div")`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(0.75)};
`;

const BlockTitle = styled(Typography)`
    font-size: 14px;
    font-weight: 500;
    color: #626262;
`;

const InfoIcon = styled(InfoOutlined)`
    font-size: 16px;
    color: #9e9e9e;
    cursor: help;
`;

const Fields = styled("div")`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1.5)};
`;

const Field = styled(TextField)`
    width: 160px;
`;

const NoteField = styled(TextField)`
    flex: 1;
    min-width: 220px;
`;

export interface IInventoryControlsProps {
    inventory: IVariantInventory;
}

export const InventoryControls: FunctionComponent<IInventoryControlsProps> = (
    props: IInventoryControlsProps,
): ReactElement => {
    const { inventory } = props;
    const variantId = inventory.variant.id;

    const message = useMessage();

    const setStatusOverrideMutation = useSetStatusOverride(variantId);
    const clearStatusOverrideMutation = useClearStatusOverride(variantId);
    const updateThresholdsMutation = useUpdateVariantThresholds(variantId);

    const [status, setStatus] = useState<StockStatus>(inventory.status);
    const [expiresAt, setExpiresAt] = useState<Date | null>(
        inventory.overrideExpiresAt
            ? new Date(inventory.overrideExpiresAt)
            : null,
    );
    const [reason, setReason] = useState<StockOverrideReason>(
        StockOverrideReason.INCORRECT_SYNC,
    );
    const [note, setNote] = useState<string>("");

    /*
     * An empty cap field means "inherit the global default", which the API
     * expresses as an explicit null.
     */
    const [floorCap, setFloorCap] = useState<string>(
        inventory.override?.floorCap?.toString() ?? "",
    );
    const [restockCap, setRestockCap] = useState<string>(
        inventory.override?.restockCap?.toString() ?? "",
    );
    const [active, setActive] = useState<boolean>(
        inventory.override?.active ?? false,
    );

    const handleApplyOverride = useCallback(async () => {
        try {
            await setStatusOverrideMutation.mutateAsync({
                status,
                expiresAt: expiresAt ? expiresAt.toISOString() : null,
                reason,
                note: note.trim(),
            });
            setNote("");
            message.showSuccess("Stock status overridden.");
        } catch (error) {
            message.showError(error);
        }
    }, [setStatusOverrideMutation, status, expiresAt, reason, note, message]);

    const handleReleaseOverride = useCallback(async () => {
        try {
            await clearStatusOverrideMutation.mutateAsync();
            message.showSuccess(
                "Override removed. The variant is back on automated sync.",
            );
        } catch (error) {
            message.showError(error);
        }
    }, [clearStatusOverrideMutation, message]);

    const handleSaveThresholds = useCallback(async () => {
        try {
            await updateThresholdsMutation.mutateAsync({
                floorCap: floorCap === "" ? null : Number(floorCap),
                restockCap: restockCap === "" ? null : Number(restockCap),
                active,
            });
            message.showSuccess("Stock thresholds updated.");
        } catch (error) {
            message.showError(error);
        }
    }, [updateThresholdsMutation, floorCap, restockCap, active, message]);

    return (
        <Root>
            <Divider />

            <Block>
                <BlockHeader>
                    <BlockTitle>Override stock status</BlockTitle>

                    <Tooltip
                        enterTouchDelay={0}
                        placement="top"
                        title="Leave the expiry empty to hold the status until someone removes it. While an override is active the automated sync cannot change the status, whatever the quantity reads. The reason is recorded against the variant and included in the alert sent to the catalogue stakeholders."
                    >
                        <InfoIcon />
                    </Tooltip>
                </BlockHeader>

                <Fields>
                    <Field
                        select={true}
                        size="small"
                        label="Status"
                        value={status}
                        onChange={(event) =>
                            setStatus(Number(event.target.value) as StockStatus)
                        }
                    >
                        <MenuItem value={StockStatus.IN_STOCK}>
                            In stock
                        </MenuItem>
                        <MenuItem value={StockStatus.OOS}>
                            Out of stock
                        </MenuItem>
                    </Field>

                    <DateTimePicker
                        label="Expires at (optional)"
                        value={expiresAt}
                        minDateTime={new Date()}
                        onChange={setExpiresAt}
                        slotProps={{
                            textField: { size: "small" },
                            field: { clearable: true },
                        }}
                    />

                    <Field
                        select={true}
                        size="small"
                        label="Reason"
                        value={reason}
                        onChange={(event) =>
                            setReason(
                                Number(
                                    event.target.value,
                                ) as StockOverrideReason,
                            )
                        }
                    >
                        {Object.entries(stockOverrideReasonMap).map(
                            ([value, label]) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ),
                        )}
                    </Field>

                    <NoteField
                        size="small"
                        label={
                            reason === StockOverrideReason.OTHER
                                ? "Note (required)"
                                : "Note (optional)"
                        }
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        error={
                            reason === StockOverrideReason.OTHER &&
                            note.trim() === ""
                        }
                    />

                    <LoadingButton
                        variant="contained"
                        size="small"
                        onClick={handleApplyOverride}
                        loading={setStatusOverrideMutation.isLoading}
                        disabled={
                            reason === StockOverrideReason.OTHER &&
                            note.trim() === ""
                        }
                    >
                        Apply
                    </LoadingButton>

                    {inventory.overrideActive && (
                        <LoadingButton
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={handleReleaseOverride}
                            loading={clearStatusOverrideMutation.isLoading}
                        >
                            Remove override
                        </LoadingButton>
                    )}
                </Fields>
            </Block>

            <Block>
                <BlockHeader>
                    <BlockTitle>Stock thresholds</BlockTitle>

                    <Tooltip
                        enterTouchDelay={0}
                        placement="top"
                        title="An empty field inherits the global default. Turning the override off falls back to the defaults without losing the values. The restock cap must be greater than the floor cap."
                    >
                        <InfoIcon />
                    </Tooltip>
                </BlockHeader>

                <Fields>
                    <Field
                        size="small"
                        type="number"
                        label="Floor cap"
                        placeholder={`Default ${inventory.defaultFloorCap}`}
                        InputLabelProps={{ shrink: true }}
                        value={floorCap}
                        onChange={(event) => setFloorCap(event.target.value)}
                    />

                    <Field
                        size="small"
                        type="number"
                        label="Restock cap"
                        placeholder={`Default ${inventory.defaultRestockCap}`}
                        InputLabelProps={{ shrink: true }}
                        value={restockCap}
                        onChange={(event) => setRestockCap(event.target.value)}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={active}
                                onChange={(event) =>
                                    setActive(event.target.checked)
                                }
                            />
                        }
                        label="Active"
                    />

                    <LoadingButton
                        variant="contained"
                        size="small"
                        onClick={handleSaveThresholds}
                        loading={updateThresholdsMutation.isLoading}
                    >
                        Save
                    </LoadingButton>
                </Fields>
            </Block>
        </Root>
    );
};
